import { useState, useEffect, useContext } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import { Col, Row } from 'react-bootstrap';
import { useFormik } from 'formik';
import {
  GenericProductGetDTO,
  QuantityGetDto,
  StockTransactionGetDto,
  AddToEmtyFormik,
  formikToAddDto,
} from 'svila-common';
import { v4 as uuid } from 'uuid';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import InventoryApi from '../../../../services/api/InventoryApi';
import InventoryStockTotals from '../../components/InventoryStockTotals';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import GenericProductsApi from '../../../../services/api/GenericProductsApi';
import InventoryAddEditForm from '../components/InventoryAddEditForm';
import StockComponent from '../components/StockComponent';
import MovementComponent from '../components/MovementComponent';
import ProductService from '../../../../services/logic/ProductService';
import { fullSchema } from '../InventorySchema';
import styled from 'styled-components';
import { Colors } from '~/src/scss/common/style';
import filterIcon from '~/src/static/img/filterIcon.svg';
import SecondaryButton from '~/src/components/common/forms/Button/SecondaryButton';
import MyContext from '~/src/services/context';
import RolesDependent from '~/src/components/common/RolesDependent';
import { ROLES } from '~/src/utils/constants';

interface ParamProps {
  warehouseId: string;
  genericProductId: string;
}

export default function InventoryForGenericInWarehouse(): JSX.Element {
  const { warehouseId, genericProductId } = useParams<ParamProps>();
  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const warehouseName = queryParams.get('warehouseName') || 'Warehouse';

  const [itemData, setItemData] = useState<GenericProductGetDTO | null>(null);
  const [movements, setMovements] = useState<StockTransactionGetDto[]>([]);
  const [stockpiles, setStockpiles] = useState<QuantityGetDto[]>([]);
  const [totals, setTotals] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [fetchingQuantities, setFetchingQuantities] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const context = useContext(MyContext);

  const [updateMovement, setUpdateMovement] = useState(false);

  useEffect(() => {
    getMovements();
  }, [updateMovement]);

  const getMovements = async (): Promise<void> => {
    const result = await InventoryApi.getByGenericInWarehouse(warehouseId, genericProductId);
    if (result.status === StatusCodes.OK) {
      setMovements(result.data);
    }
  };

  const getStockPiles = async (): Promise<void> => {
    setFetchingQuantities(true);
    const result = await InventoryApi.getQuantitiesForGenericInWarehouse(warehouseId, genericProductId);
    if (result.status === StatusCodes.OK) {
      setStockpiles(result.data);
    }
    setFetchingQuantities(false);
  };

  const getItem = async (): Promise<void> => {
    const result = await GenericProductsApi.get(genericProductId);
    if (result.status === StatusCodes.OK) {
      setItemData(result.data);
    }
  };

  const getTotals = async (): Promise<void> => {
    const result = await InventoryApi.getTotalsByGenericInWarehouse(warehouseId, genericProductId);
    if (result.status === StatusCodes.OK) {
      setTotals(result.data);
    }
  };

  const initialValues = AddToEmtyFormik();
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: fullSchema,
    onSubmit: async (values) => {
      setFetchingQuantities(true);
      const converted = formikToAddDto(values, genericProductId);
      const result = await InventoryApi.getQuantitiesWithFilterByGenericInWarehouse(
        warehouseId,
        genericProductId,
        converted
      );
      if (result.status === StatusCodes.OK) {
        setStockpiles(result.data);
      }
      setFetchingQuantities(false);
    },
  });

  useEffect(() => {
    if (genericProductId && warehouseId) {
      getItem();
      getTotals();
      getMovements();
      getStockPiles();
    }
  }, [genericProductId, warehouseId]);

  const TabButton = ({ active, onClick, children }) => {
    const styles = {
      background: active ? '#F4F4F4' : '#E7E7E7',
      cursor: 'pointer',
      padding: '10px',
      marginRight: '5px',
      borderTopRightRadius: 10,
      borderTopLeftRadius: 10,
      width: '230px',
      textAlign: 'center',
    };

    return (
      <div style={styles} className={`tab ${active ? 'active' : ''}`} onClick={onClick}>
        <TabStyleP>{children}</TabStyleP>
      </div>
    );
  };

  return (
    <WhitePad isFluid>
      <></>
      {itemData && (
        <Col>
          <TitleComponent
            onLeftHandler={() => {
              history.goBack();
            }}
            title={`${itemData?.name || 'Generic Product'} in ${warehouseName}`}
          />
          <Row style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 70 }}>
            <ManufacturerTable xs={3}>
              <Row style={{ color: '#EF7D00', marginBottom: 5 }}>
                <OrangeText style={{ textAlign: 'center', margin: '0 auto' }}>Manufacturer</OrangeText>
              </Row>
              <Row>
                <WhiteText style={{ textAlign: 'center', margin: '0 auto', paddingBottom: '20px' }}>
                  {itemData.products[0].manufacturer.name}
                </WhiteText>
              </Row>
              <Row style={{ flexWrap: 'nowrap' }}>
                <Col style={{ flexGrow: '0' }}>
                  <Row
                    style={{ borderBottom: '1px solid #59231F', borderRight: '1px solid #59231F', height: '35px' }}
                  >
                    <OrangeText>CU</OrangeText>
                  </Row>
                  <Row
                    style={{ borderBottom: '1px solid #59231F', borderRight: '1px solid #59231F', height: '35px' }}
                  >
                    <OrangeText>DP</OrangeText>
                  </Row>
                  <Row style={{ borderRight: '1px solid #59231F', height: '35px' }}>
                    <OrangeText>DU</OrangeText>
                  </Row>
                </Col>
                {itemData.products.map((product) => {
                  return (
                    <Col>
                      <Row style={{ borderBottom: '1px solid #59231F', height: '35px' }}>
                        <WhiteText>{ProductService.getCuGtin(product.packagedProducts)}</WhiteText>
                      </Row>
                      <Row style={{ borderBottom: '1px solid #59231F', height: '35px' }}>
                        <WhiteText>{ProductService.getDisplayGtin(product.packagedProducts)}</WhiteText>
                      </Row>
                      <Row style={{ height: '35px' }}>
                        <WhiteText>{ProductService.getDuGtin(product.packagedProducts)}</WhiteText>
                      </Row>
                    </Col>
                  );
                })}
              </Row>
            </ManufacturerTable>
            <Col xs={9}> {totals && <InventoryStockTotals totals={totals} />}</Col>
          </Row>
          <div className="accordion-container">
            <TabsWrapper>
              <TabButton active={activeTab === 1} onClick={() => setActiveTab(1)}>
                Stock
              </TabButton>

              <RolesDependent
                roles={[
                  ROLES.ADMIN,
                  ROLES.SUPER_ADMIN,
                  ROLES.WAREHOUSE_ADMIN,
                  ROLES.WAREHOUSE,
                  ROLES.OFFICE,
                  ROLES.SALES,
                  ROLES.DATABASE_ADMIN,
                  ROLES.SVILA_CLIENT_ADMIN,
                  ROLES.SVILA_CLIENT,
                ]}
              >
                <TabButton active={activeTab === 2} onClick={() => setActiveTab(2)}>
                  Movements
                </TabButton>
              </RolesDependent>
            </TabsWrapper>
            <WrapperBox>
              {activeTab === 1 && stockpiles && (
                <Row>
                  <Col>
                    {stockpiles && stockpiles.length === 0 ? <h3>No Stock</h3> : null}
                    <>
                      {stockpiles?.map((item) => (
                        <StockComponent item={item} key={uuid()} />
                      ))}
                    </>
                  </Col>
                </Row>
              )}
              <RolesDependent
                roles={[
                  ROLES.ADMIN,
                  ROLES.SUPER_ADMIN,
                  ROLES.WAREHOUSE_ADMIN,
                  ROLES.WAREHOUSE,
                  ROLES.OFFICE,
                  ROLES.SALES,
                  ROLES.DATABASE_ADMIN,
                  ROLES.SVILA_CLIENT_ADMIN,
                  ROLES.SVILA_CLIENT,
                ]}
              >
                {activeTab === 2 && movements && (
                  <Row>
                    <Col>
                      {movements && movements.length === 0 ? <h3>No Movements</h3> : null}
                      <>
                        <ActionWrapper>
                          <SecondaryButton
                            onClick={() => setFilterOpen(!filterOpen)}
                            icon={filterIcon}
                            iconSize={12}
                            title="Filter"
                          />
                        </ActionWrapper>
                        <div>
                          {filterOpen && (
                            <InventoryAddEditForm formikProps={formik} generic={itemData} values={null} />
                          )}
                        </div>
                        {movements?.map((item) => (
                          <div>
                            <MovementComponent
                              item={item}
                              key={uuid()}
                              updateMovemnt={updateMovement}
                              setUpdateMovemnt={setUpdateMovement}
                            />
                          </div>
                        ))}
                      </>
                    </Col>
                  </Row>
                )}
              </RolesDependent>
            </WrapperBox>
          </div>
        </Col>
      )}
    </WhitePad>
  );
}

const ActionWrapper = styled.div`
  display: flex;
  justify-content: end;
  padding: 35px 10px;
`;

const WrapperBox = styled.div`
  background: #f4f4f4;
  width: 100%;
`;

const TabsWrapper = styled.div`
  display: flex;
`;

const TabStyleP = styled.span`
  color: #642b26;
`;

const ManufacturerTable = styled(Col)`
  background: ${Colors.darkBrown};
  padding: 30px 60px;
  border-radius: 10px;
  max-width: 300px;
`;

const WhiteText = styled.span`
  color: #fff;
  padding: 0 7px;
  display: flex;
  align-items: center;
`;

const OrangeText = styled.p`
  color: ${Colors.newOrage};
  margin: 0;
  padding: 0 7px;
  border: 0;
  display: flex;
  align-items: center;
`;
